import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { COLORS } from '../../pages/app/reports/groups-teams-activity/constants';
import { UserRow } from '../../pages/app/reports/groups-teams-activity/types';
import { StatusDot } from './status-dot';
import { TagPills } from './tag-pills';

interface UsersTableProps {
    users: UserRow[];
    totalCount: number;
}

const thStyle = {
    fontSize: '10px',
    color: COLORS.grayMedium,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '.5px',
    padding: '8px 12px',
    textAlign: 'left' as const,
    borderBottom: `2px solid ${COLORS.border}`,
    whiteSpace: 'nowrap' as const,
};

const tdStyle = {
    padding: '10px 12px',
    borderBottom: `1px solid ${COLORS.borderLight}`,
    verticalAlign: 'middle' as const,
    fontSize: '13px',
};

const StatusBadge: React.FC<{ status: 'active' | 'inactive'; daysActive: number }> = ({ status, daysActive }) => {
    let bg: string, color: string, label: string, dotColor: string;

    if (status === 'inactive') {
        bg = COLORS.grayLight;
        color = COLORS.grayDark;
        label = 'Inactive';
        dotColor = COLORS.gray;
    } else if (daysActive === 1) {
        bg = '#FEF3C7';
        color = '#92400E';
        label = 'Low';
        dotColor = COLORS.yellow;
    } else {
        bg = '#D1FAE5';
        color = '#065F46';
        label = 'Active';
        dotColor = COLORS.green;
    }

    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '11px',
                fontWeight: 500,
                padding: '2px 8px',
                borderRadius: '10px',
                backgroundColor: bg,
                color: color,
            }}
        >
            <StatusDot color={dotColor} size={6} />
            {label}
        </Box>
    );
};

export const UsersTable: React.FC<UsersTableProps> = ({ users, totalCount }) => {
    const ZeroVal: React.FC<{ value: number; isInactive: boolean }> = ({ value, isInactive }) => (
        <span style={{ color: isInactive ? COLORS.gray : undefined }}>{value}</span>
    );

    return (
        <Box
            sx={{
                backgroundColor: COLORS.cardBg,
                borderRadius: '10px',
                border: `1px solid ${COLORS.border}`,
                overflow: 'hidden',
            }}
        >
            {/* Table */}
            <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={{ ...thStyle, width: '180px' }}>User</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Days Active</th>
                            <th style={{ ...thStyle, textAlign: 'center' }}>SOS</th>
                            <th style={{ ...thStyle, textAlign: 'center' }}>Fake Call</th>
                            <th style={{ ...thStyle, textAlign: 'center' }}>Check On Me</th>
                            <th style={{ ...thStyle, textAlign: 'center' }}>Safety Reports</th>
                            <th style={{ ...thStyle, textAlign: 'center' }}>Risk Reviews</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Last Active</th>
                            <th style={thStyle}>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                    Status
                                    <Tooltip
                                        title={
                                            <Box sx={{ fontSize: '11px', lineHeight: 1.5, padding: '2px 0' }}>
                                                <strong>Active:</strong> 2+ days of usage in the period<br />
                                                <strong>Low:</strong> Only 1 day of usage in the period<br />
                                                <strong>Inactive:</strong> No usage in the period
                                            </Box>
                                        }
                                        arrow
                                        placement="top"
                                    >
                                        <InfoOutlinedIcon sx={{ fontSize: '12px', color: COLORS.grayMedium, cursor: 'help' }} />
                                    </Tooltip>
                                </Box>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user) => {
                            const isInactive = user.status === 'inactive';
                            return (
                                <tr
                                    key={user.id}
                                    style={{
                                        color: isInactive ? '#A0AEC0' : undefined,
                                    }}
                                >
                                    <td style={tdStyle}>
                                        <Box>
                                            <Typography
                                                sx={{
                                                    fontWeight: 500,
                                                    fontSize: '13px',
                                                    color: isInactive ? '#A0AEC0' : COLORS.textPrimary,
                                                }}
                                            >
                                                {user.name}
                                            </Typography>
                                            <TagPills tags={user.tags} />
                                        </Box>
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right', color: isInactive ? '#A0AEC0' : undefined }}>
                                        <ZeroVal value={user.daysActive} isInactive={isInactive} />
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'center', color: isInactive ? '#A0AEC0' : undefined }}>
                                        <ZeroVal value={user.sosCount} isInactive={isInactive} />
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'center', color: isInactive ? '#A0AEC0' : undefined }}>
                                        <ZeroVal value={user.fakeCallCount} isInactive={isInactive} />
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'center', color: isInactive ? '#A0AEC0' : undefined }}>
                                        <ZeroVal value={user.checkOnMeCount} isInactive={isInactive} />
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'center', color: isInactive ? '#A0AEC0' : undefined }}>
                                        <ZeroVal value={user.safetyReportCount} isInactive={isInactive} />
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'center', color: isInactive ? '#A0AEC0' : undefined }}>
                                        <ZeroVal value={user.riskReviewCount} isInactive={isInactive} />
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right', fontSize: '12px', color: COLORS.grayDark }}>
                                        {user.lastActive}
                                    </td>
                                    <td style={tdStyle}>
                                        <StatusBadge status={user.status} daysActive={user.daysActive} />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 18px',
                    fontSize: '11px',
                    color: COLORS.grayDark,
                    borderTop: `1px solid ${COLORS.border}`,
                }}
            >
                <Typography sx={{ fontSize: '11px', color: COLORS.grayDark }}>
                    Showing {users.length} of {totalCount} users
                </Typography>
                <Box sx={{ display: 'flex', gap: '8px' }}>
                    <Typography sx={{ fontSize: '11px', color: COLORS.grayMedium, fontWeight: 500 }}>
                        &#8592; Prev
                    </Typography>
                    <Typography sx={{ color: COLORS.grayMedium }}>|</Typography>
                    <Typography sx={{ fontSize: '11px', color: COLORS.grayMedium, fontWeight: 500 }}>
                        Next &#8594;
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};
